// ─────────────────────────────────────────────────────────────────────────────
// SurvAIHealth EHR — Behavioral-health assessment instrument library.
//
// This module defines the standardized, federally-relevant screening and
// measurement instruments used across behavioral / mental-health and SUD care.
// Each instrument is a self-describing definition (items + options + scoring +
// interpretation bands) so the generic <AssessmentRunner> can render and score
// any of them without instrument-specific UI code.
//
// SCORING MODEL
//   scoring.method = "sum"     → sum of selected option values, mapped to bands
//   scoring.method = "custom"  → scoring.fn(answers, instrument) → result object
//   A scoring result is always: { score, max, band, interpretation, flags[] }
//
// PROVENANCE
//   Instruments marked publicDomain:true use public-domain item text (PHQ/GAD
//   © Pfizer, freely reproducible; C-SSRS, PCL-5, AUDIT, DAST, CAGE, etc. are
//   public-domain / freely usable for clinical care). Instruments marked
//   licensed:true (CANS, ANSA, ASAM, MoCA) require a license — their structure
//   is scaffolded and flagged so the org can attach the licensed content.
//
// NOTE: These tools support clinical workflow; they do not replace clinical
// judgment. Positive screens require clinical follow-up.
// ─────────────────────────────────────────────────────────────────────────────

// Reusable option scales ------------------------------------------------------
const PHQ_SCALE = [
  { label: "Not at all", value: 0 },
  { label: "Several days", value: 1 },
  { label: "More than half the days", value: 2 },
  { label: "Nearly every day", value: 3 },
];

const YES_NO = [
  { label: "No", value: 0 },
  { label: "Yes", value: 1 },
];

const PCL5_SCALE = [
  { label: "Not at all", value: 0 },
  { label: "A little bit", value: 1 },
  { label: "Moderately", value: 2 },
  { label: "Quite a bit", value: 3 },
  { label: "Extremely", value: 4 },
];

// Helper to build a simple summed band lookup --------------------------------
function bandFor(bands, score) {
  return bands.find((b) => score >= b.min && score <= b.max) || null;
}

function sumScorer(bands, opts = {}) {
  return function score(answers, inst) {
    const items = inst.items.filter((i) => !i.excludeFromScore);
    let total = 0;
    let answered = 0;
    for (const it of items) {
      const v = answers[it.id];
      if (v === undefined || v === null || v === "") continue;
      total += Number(v);
      answered += 1;
    }
    const max = items.reduce((m, it) => m + Math.max(...(it.options || [{ value: 0 }]).map((o) => o.value)), 0);
    const band = bandFor(bands, total);
    const flags = [];
    if (opts.flagItem && answers[opts.flagItem.id] >= opts.flagItem.threshold) {
      flags.push(opts.flagItem.message);
    }
    return {
      score: total,
      max,
      answered,
      total: items.length,
      band: band?.label || "—",
      color: band?.color || "#64748b",
      interpretation: band?.interpretation || "",
      flags,
    };
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DEPRESSION
// ─────────────────────────────────────────────────────────────────────────────
const PHQ9 = {
  id: "phq9",
  name: "PHQ-9",
  fullName: "Patient Health Questionnaire-9 (Depression)",
  domain: "Depression",
  ageRange: "Adult (18+)",
  estMinutes: 3,
  publicDomain: true,
  federalUse: "CMS MIPS depression remission (eCQM CMS159); CCBHC; SBIRT; measurement-based care",
  prompt: "Over the last 2 weeks, how often have you been bothered by any of the following problems?",
  items: [
    { id: "q1", text: "Little interest or pleasure in doing things", options: PHQ_SCALE },
    { id: "q2", text: "Feeling down, depressed, or hopeless", options: PHQ_SCALE },
    { id: "q3", text: "Trouble falling or staying asleep, or sleeping too much", options: PHQ_SCALE },
    { id: "q4", text: "Feeling tired or having little energy", options: PHQ_SCALE },
    { id: "q5", text: "Poor appetite or overeating", options: PHQ_SCALE },
    { id: "q6", text: "Feeling bad about yourself — or that you are a failure or have let yourself or your family down", options: PHQ_SCALE },
    { id: "q7", text: "Trouble concentrating on things, such as reading the newspaper or watching television", options: PHQ_SCALE },
    { id: "q8", text: "Moving or speaking so slowly that other people could have noticed — or the opposite, being so fidgety or restless that you have been moving around a lot more than usual", options: PHQ_SCALE },
    { id: "q9", text: "Thoughts that you would be better off dead, or of hurting yourself in some way", options: PHQ_SCALE },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 4, label: "Minimal", color: "#16a34a", interpretation: "Minimal depression. Monitor; may not require treatment." },
      { min: 5, max: 9, label: "Mild", color: "#65a30d", interpretation: "Mild depression. Watchful waiting; repeat PHQ-9 at follow-up." },
      { min: 10, max: 14, label: "Moderate", color: "#ca8a04", interpretation: "Moderate depression. Consider treatment plan (therapy and/or pharmacotherapy)." },
      { min: 15, max: 19, label: "Moderately Severe", color: "#ea580c", interpretation: "Moderately severe depression. Active treatment with pharmacotherapy and/or psychotherapy." },
      { min: 20, max: 27, label: "Severe", color: "#dc2626", interpretation: "Severe depression. Immediate initiation of pharmacotherapy and expedited referral." },
    ],
    flagItem: { id: "q9", threshold: 1, message: "Item 9 endorsed — administer C-SSRS suicide risk assessment now." },
  },
};

const PHQ2 = {
  id: "phq2",
  name: "PHQ-2",
  fullName: "Patient Health Questionnaire-2 (Depression pre-screen)",
  domain: "Depression",
  ageRange: "Adult (18+)",
  estMinutes: 1,
  publicDomain: true,
  federalUse: "USPSTF depression screening; primary-care pre-screen prior to PHQ-9",
  prompt: "Over the last 2 weeks, how often have you been bothered by the following problems?",
  items: [
    { id: "q1", text: "Little interest or pleasure in doing things", options: PHQ_SCALE },
    { id: "q2", text: "Feeling down, depressed, or hopeless", options: PHQ_SCALE },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 2, label: "Negative", color: "#16a34a", interpretation: "Negative screen. No further action indicated at this time." },
      { min: 3, max: 6, label: "Positive", color: "#dc2626", interpretation: "Positive screen (≥3). Administer the full PHQ-9." },
    ],
  },
};

const GDS15 = {
  id: "gds15",
  name: "GDS-15",
  fullName: "Geriatric Depression Scale (Short Form)",
  domain: "Depression",
  ageRange: "Older adult (65+)",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "Geriatric/long-term-care depression screening; PHQ-9 alternative for elderly",
  prompt: "Choose the best answer for how you have felt over the past week.",
  items: [
    { id: "q1", text: "Are you basically satisfied with your life?", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }] },
    { id: "q2", text: "Have you dropped many of your activities and interests?", options: YES_NO },
    { id: "q3", text: "Do you feel that your life is empty?", options: YES_NO },
    { id: "q4", text: "Do you often get bored?", options: YES_NO },
    { id: "q5", text: "Are you in good spirits most of the time?", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }] },
    { id: "q6", text: "Are you afraid that something bad is going to happen to you?", options: YES_NO },
    { id: "q7", text: "Do you feel happy most of the time?", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }] },
    { id: "q8", text: "Do you often feel helpless?", options: YES_NO },
    { id: "q9", text: "Do you prefer to stay at home, rather than going out and doing new things?", options: YES_NO },
    { id: "q10", text: "Do you feel you have more problems with memory than most?", options: YES_NO },
    { id: "q11", text: "Do you think it is wonderful to be alive now?", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }] },
    { id: "q12", text: "Do you feel pretty worthless the way you are now?", options: YES_NO },
    { id: "q13", text: "Do you feel full of energy?", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }] },
    { id: "q14", text: "Do you feel that your situation is hopeless?", options: YES_NO },
    { id: "q15", text: "Do you think that most people are better off than you are?", options: YES_NO },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 4, label: "Normal", color: "#16a34a", interpretation: "Within normal range." },
      { min: 5, max: 8, label: "Mild", color: "#ca8a04", interpretation: "Suggestive of mild depression. Clinical follow-up indicated." },
      { min: 9, max: 11, label: "Moderate", color: "#ea580c", interpretation: "Suggestive of moderate depression." },
      { min: 12, max: 15, label: "Severe", color: "#dc2626", interpretation: "Suggestive of severe depression." },
    ],
  },
};

const EPDS = {
  id: "epds",
  name: "EPDS",
  fullName: "Edinburgh Postnatal Depression Scale",
  domain: "Depression (Perinatal)",
  ageRange: "Perinatal / postpartum",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "HRSA/Medicaid maternal depression screening; perinatal measurement-based care",
  prompt: "As you are pregnant or have recently had a baby, please indicate how you have felt in the past 7 days.",
  items: [
    { id: "q1", text: "I have been able to laugh and see the funny side of things", options: [{ label: "As much as I always could", value: 0 }, { label: "Not quite so much now", value: 1 }, { label: "Definitely not so much now", value: 2 }, { label: "Not at all", value: 3 }] },
    { id: "q2", text: "I have looked forward with enjoyment to things", options: [{ label: "As much as I ever did", value: 0 }, { label: "Rather less than I used to", value: 1 }, { label: "Definitely less than I used to", value: 2 }, { label: "Hardly at all", value: 3 }] },
    { id: "q3", text: "I have blamed myself unnecessarily when things went wrong", options: [{ label: "No, never", value: 0 }, { label: "Not very often", value: 1 }, { label: "Yes, some of the time", value: 2 }, { label: "Yes, most of the time", value: 3 }] },
    { id: "q4", text: "I have been anxious or worried for no good reason", options: [{ label: "No, not at all", value: 0 }, { label: "Hardly ever", value: 1 }, { label: "Yes, sometimes", value: 2 }, { label: "Yes, very often", value: 3 }] },
    { id: "q5", text: "I have felt scared or panicky for no very good reason", options: [{ label: "No, not at all", value: 0 }, { label: "No, not much", value: 1 }, { label: "Yes, sometimes", value: 2 }, { label: "Yes, quite a lot", value: 3 }] },
    { id: "q6", text: "Things have been getting on top of me", options: [{ label: "No, I have been coping as well as ever", value: 0 }, { label: "No, most of the time I have coped quite well", value: 1 }, { label: "Yes, sometimes I have not been coping as well as usual", value: 2 }, { label: "Yes, most of the time I have not been able to cope at all", value: 3 }] },
    { id: "q7", text: "I have been so unhappy that I have had difficulty sleeping", options: [{ label: "No, not at all", value: 0 }, { label: "Not very often", value: 1 }, { label: "Yes, sometimes", value: 2 }, { label: "Yes, most of the time", value: 3 }] },
    { id: "q8", text: "I have felt sad or miserable", options: [{ label: "No, not at all", value: 0 }, { label: "Not very often", value: 1 }, { label: "Yes, quite often", value: 2 }, { label: "Yes, most of the time", value: 3 }] },
    { id: "q9", text: "I have been so unhappy that I have been crying", options: [{ label: "No, never", value: 0 }, { label: "Only occasionally", value: 1 }, { label: "Yes, quite often", value: 2 }, { label: "Yes, most of the time", value: 3 }] },
    { id: "q10", text: "The thought of harming myself has occurred to me", options: [{ label: "Never", value: 0 }, { label: "Hardly ever", value: 1 }, { label: "Sometimes", value: 2 }, { label: "Yes, quite often", value: 3 }] },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 9, label: "Low likelihood", color: "#16a34a", interpretation: "Depression unlikely. Re-screen per protocol." },
      { min: 10, max: 12, label: "Possible", color: "#ca8a04", interpretation: "Possible depression. Repeat in 2 weeks / clinical evaluation." },
      { min: 13, max: 30, label: "Probable", color: "#dc2626", interpretation: "Probable depression. Further clinical evaluation indicated." },
    ],
    flagItem: { id: "q10", threshold: 1, message: "Item 10 (self-harm) endorsed — administer C-SSRS now regardless of total score." },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ANXIETY
// ─────────────────────────────────────────────────────────────────────────────
const GAD7 = {
  id: "gad7",
  name: "GAD-7",
  fullName: "Generalized Anxiety Disorder-7",
  domain: "Anxiety",
  ageRange: "Adult (18+)",
  estMinutes: 3,
  publicDomain: true,
  federalUse: "Measurement-based care; CCBHC; common pairing with PHQ-9",
  prompt: "Over the last 2 weeks, how often have you been bothered by the following problems?",
  items: [
    { id: "q1", text: "Feeling nervous, anxious, or on edge", options: PHQ_SCALE },
    { id: "q2", text: "Not being able to stop or control worrying", options: PHQ_SCALE },
    { id: "q3", text: "Worrying too much about different things", options: PHQ_SCALE },
    { id: "q4", text: "Trouble relaxing", options: PHQ_SCALE },
    { id: "q5", text: "Being so restless that it is hard to sit still", options: PHQ_SCALE },
    { id: "q6", text: "Becoming easily annoyed or irritable", options: PHQ_SCALE },
    { id: "q7", text: "Feeling afraid, as if something awful might happen", options: PHQ_SCALE },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 4, label: "Minimal", color: "#16a34a", interpretation: "Minimal anxiety." },
      { min: 5, max: 9, label: "Mild", color: "#65a30d", interpretation: "Mild anxiety. Monitor." },
      { min: 10, max: 14, label: "Moderate", color: "#ea580c", interpretation: "Moderate anxiety. Possible clinically significant condition — further evaluation." },
      { min: 15, max: 21, label: "Severe", color: "#dc2626", interpretation: "Severe anxiety. Active treatment warranted." },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SUICIDE RISK
// ─────────────────────────────────────────────────────────────────────────────
const CSSRS = {
  id: "cssrs",
  name: "C-SSRS",
  fullName: "Columbia-Suicide Severity Rating Scale (Screener)",
  domain: "Suicide Risk",
  ageRange: "All ages",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "Joint Commission NPSG 15.01.01; CMS/SAMHSA Zero Suicide; required suicide-risk screening",
  prompt: "Ask questions 1 and 2. If 'No' to both, go directly to question 6. If 'Yes' to question 2, ask 3, 4 and 5. Ask question 6 of everyone.",
  items: [
    { id: "q1", text: "1. Have you wished you were dead or wished you could go to sleep and not wake up? (Past month)", options: YES_NO },
    { id: "q2", text: "2. Have you actually had any thoughts of killing yourself? (Past month)", options: YES_NO },
    { id: "q3", text: "3. Have you been thinking about how you might do this?", options: YES_NO, conditional: { on: "q2", equals: 1 } },
    { id: "q4", text: "4. Have you had these thoughts and had some intention of acting on them?", options: YES_NO, conditional: { on: "q2", equals: 1 } },
    { id: "q5", text: "5. Have you started to work out or worked out the details of how to kill yourself? Do you intend to carry out this plan?", options: YES_NO, conditional: { on: "q2", equals: 1 } },
    { id: "q6", text: "6. Have you ever done anything, started to do anything, or prepared to do anything to end your life? (Lifetime, and within past 3 months)", options: [{ label: "No", value: 0 }, { label: "Yes — more than 3 months ago", value: 1 }, { label: "Yes — within past 3 months", value: 2 }] },
  ],
  scoring: {
    method: "custom",
    fn: function (a) {
      const flags = [];
      let level = "No risk identified";
      let color = "#16a34a";
      let interpretation = "No suicidal ideation or behavior endorsed on screener.";
      const q1 = a.q1, q2 = a.q2, q3 = a.q3, q4 = a.q4, q5 = a.q5, q6 = a.q6;
      // Behavior takes precedence
      if (q6 === 2) {
        level = "HIGH RISK"; color = "#dc2626";
        interpretation = "Suicidal behavior within the past 3 months. Initiate behavioral-health/safety protocol, do not leave patient alone, consider emergency evaluation.";
        flags.push("Recent suicidal behavior — high risk protocol");
      } else if (q5 === 1) {
        level = "HIGH RISK"; color = "#dc2626";
        interpretation = "Active ideation with specific plan and intent. Initiate safety protocol and arrange urgent psychiatric evaluation.";
        flags.push("Plan + intent — high risk protocol");
      } else if (q4 === 1) {
        level = "HIGH RISK"; color = "#dc2626";
        interpretation = "Active ideation with some intent to act, without specific plan. Initiate safety protocol; urgent evaluation.";
        flags.push("Ideation with intent — high risk protocol");
      } else if (q3 === 1) {
        level = "MODERATE RISK"; color = "#ea580c";
        interpretation = "Active ideation with method (no plan/intent). Behavioral-health evaluation and safety planning indicated.";
        flags.push("Ideation with method — moderate risk");
      } else if (q2 === 1 || q6 === 1) {
        level = "LOW–MODERATE RISK"; color = "#ca8a04";
        interpretation = "Suicidal thoughts present and/or lifetime behavior >3 months ago. Safety planning and clinical follow-up indicated.";
        flags.push("Ideation present — safety planning indicated");
      } else if (q1 === 1) {
        level = "LOW RISK"; color = "#65a30d";
        interpretation = "Passive death wish. Monitor; document and reassess.";
      }
      return { score: level, max: null, band: level, color, interpretation, flags, isRiskScale: true };
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ALCOHOL / SUBSTANCE USE  (SBIRT)
// ─────────────────────────────────────────────────────────────────────────────
const AUDITC = {
  id: "auditc",
  name: "AUDIT-C",
  fullName: "Alcohol Use Disorders Identification Test (Consumption)",
  domain: "Alcohol Use",
  ageRange: "Adult (18+)",
  estMinutes: 2,
  publicDomain: true,
  federalUse: "SBIRT (SAMHSA); unhealthy alcohol use screening (USPSTF); sex-specific cutoffs",
  prompt: "Please answer about your alcohol use.",
  collectsSex: true,
  items: [
    { id: "q1", text: "How often do you have a drink containing alcohol?", options: [{ label: "Never", value: 0 }, { label: "Monthly or less", value: 1 }, { label: "2–4 times a month", value: 2 }, { label: "2–3 times a week", value: 3 }, { label: "4 or more times a week", value: 4 }] },
    { id: "q2", text: "How many standard drinks containing alcohol do you have on a typical day when drinking?", options: [{ label: "1 or 2", value: 0 }, { label: "3 or 4", value: 1 }, { label: "5 or 6", value: 2 }, { label: "7 to 9", value: 3 }, { label: "10 or more", value: 4 }] },
    { id: "q3", text: "How often do you have six or more drinks on one occasion?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
  ],
  scoring: {
    method: "custom",
    fn: function (a, inst, ctx = {}) {
      const total = (Number(a.q1) || 0) + (Number(a.q2) || 0) + (Number(a.q3) || 0);
      const sex = ctx.sex || "unspecified";
      const cutoff = sex === "female" ? 3 : 4;
      const positive = total >= cutoff;
      return {
        score: total, max: 12,
        band: positive ? "Positive" : "Negative",
        color: positive ? "#dc2626" : "#16a34a",
        interpretation: positive
          ? `Positive for unhealthy alcohol use (≥${cutoff} for ${sex === "unspecified" ? "this patient" : sex}). Proceed to brief intervention; consider full AUDIT and SUD assessment.`
          : `Below threshold (cutoff ≥${cutoff}). Provide positive reinforcement / re-screen per protocol.`,
        flags: positive ? ["Positive alcohol screen — brief intervention indicated (SBIRT)"] : [],
      };
    },
  },
};

const AUDIT = {
  id: "audit",
  name: "AUDIT",
  fullName: "Alcohol Use Disorders Identification Test (Full 10-item)",
  domain: "Alcohol Use",
  ageRange: "Adult (18+)",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "WHO/SAMHSA full alcohol screen following positive AUDIT-C",
  prompt: "Please answer the following questions about your alcohol use.",
  items: [
    { id: "q1", text: "How often do you have a drink containing alcohol?", options: [{ label: "Never", value: 0 }, { label: "Monthly or less", value: 1 }, { label: "2–4 times a month", value: 2 }, { label: "2–3 times a week", value: 3 }, { label: "4+ times a week", value: 4 }] },
    { id: "q2", text: "How many drinks containing alcohol do you have on a typical day when you are drinking?", options: [{ label: "1 or 2", value: 0 }, { label: "3 or 4", value: 1 }, { label: "5 or 6", value: 2 }, { label: "7 to 9", value: 3 }, { label: "10 or more", value: 4 }] },
    { id: "q3", text: "How often do you have six or more drinks on one occasion?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "q4", text: "How often during the last year have you found that you were not able to stop drinking once you had started?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "q5", text: "How often during the last year have you failed to do what was normally expected of you because of drinking?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "q6", text: "How often during the last year have you needed a first drink in the morning to get yourself going after a heavy drinking session?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "q7", text: "How often during the last year have you had a feeling of guilt or remorse after drinking?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "q8", text: "How often during the last year have you been unable to remember what happened the night before because of your drinking?", options: [{ label: "Never", value: 0 }, { label: "Less than monthly", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "q9", text: "Have you or someone else been injured because of your drinking?", options: [{ label: "No", value: 0 }, { label: "Yes, but not in the last year", value: 2 }, { label: "Yes, during the last year", value: 4 }] },
    { id: "q10", text: "Has a relative, friend, doctor, or other health worker been concerned about your drinking or suggested you cut down?", options: [{ label: "No", value: 0 }, { label: "Yes, but not in the last year", value: 2 }, { label: "Yes, during the last year", value: 4 }] },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 7, label: "Low risk", color: "#16a34a", interpretation: "Zone I — low-risk drinking or abstinence. Alcohol education." },
      { min: 8, max: 15, label: "Hazardous", color: "#ca8a04", interpretation: "Zone II — hazardous use. Simple advice / brief intervention." },
      { min: 16, max: 19, label: "Harmful", color: "#ea580c", interpretation: "Zone III — harmful use. Brief intervention + continued monitoring." },
      { min: 20, max: 40, label: "Possible dependence", color: "#dc2626", interpretation: "Zone IV — possible dependence. Refer to specialist for diagnostic evaluation and treatment." },
    ],
  },
};

const DAST10 = {
  id: "dast10",
  name: "DAST-10",
  fullName: "Drug Abuse Screening Test (10-item)",
  domain: "Drug Use",
  ageRange: "Adult (18+)",
  estMinutes: 3,
  publicDomain: true,
  federalUse: "SBIRT drug-use screening (SAMHSA)",
  prompt: 'These questions refer to drug use in the past 12 months (not including alcoholic beverages). "Drug use" refers to non-medical use of prescription drugs and illegal drug use.',
  items: [
    { id: "q1", text: "Have you used drugs other than those required for medical reasons?", options: YES_NO },
    { id: "q2", text: "Do you abuse more than one drug at a time?", options: YES_NO },
    { id: "q3", text: "Are you always able to stop using drugs when you want to? (If never use drugs, answer Yes)", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }] },
    { id: "q4", text: "Have you had blackouts or flashbacks as a result of drug use?", options: YES_NO },
    { id: "q5", text: "Do you ever feel bad or guilty about your drug use? (If never use, answer No)", options: YES_NO },
    { id: "q6", text: "Does your spouse (or parents) ever complain about your involvement with drugs?", options: YES_NO },
    { id: "q7", text: "Have you neglected your family because of your use of drugs?", options: YES_NO },
    { id: "q8", text: "Have you engaged in illegal activities in order to obtain drugs?", options: YES_NO },
    { id: "q9", text: "Have you ever experienced withdrawal symptoms (felt sick) when you stopped taking drugs?", options: YES_NO },
    { id: "q10", text: "Have you had medical problems as a result of your drug use (e.g., memory loss, hepatitis, convulsions, bleeding)?", options: YES_NO },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 0, label: "No problems", color: "#16a34a", interpretation: "No problems reported. Monitor / re-screen." },
      { min: 1, max: 2, label: "Low level", color: "#65a30d", interpretation: "Low level. Brief advice / monitor." },
      { min: 3, max: 5, label: "Moderate level", color: "#ca8a04", interpretation: "Moderate level. Further investigation / brief intervention." },
      { min: 6, max: 8, label: "Substantial level", color: "#ea580c", interpretation: "Substantial level. Intensive assessment and treatment." },
      { min: 9, max: 10, label: "Severe level", color: "#dc2626", interpretation: "Severe level. Intensive assessment and treatment referral." },
    ],
  },
};

const NIDA_QS = {
  id: "nida_qs",
  name: "NIDA Quick Screen",
  fullName: "NIDA Quick Screen (Past-year substance use)",
  domain: "Substance Use",
  ageRange: "Adult (18+)",
  estMinutes: 2,
  publicDomain: true,
  federalUse: "NIDA-modified ASSIST first step; SBIRT",
  prompt: "In the past year, how often have you used the following?",
  items: [
    { id: "alcohol", text: "Alcohol (men: 5+ / women: 4+ drinks a day)", options: [{ label: "Never", value: 0 }, { label: "Once or twice", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "tobacco", text: "Tobacco products", options: [{ label: "Never", value: 0 }, { label: "Once or twice", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "rx", text: "Prescription drugs for non-medical reasons", options: [{ label: "Never", value: 0 }, { label: "Once or twice", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
    { id: "illegal", text: "Illegal drugs", options: [{ label: "Never", value: 0 }, { label: "Once or twice", value: 1 }, { label: "Monthly", value: 2 }, { label: "Weekly", value: 3 }, { label: "Daily or almost daily", value: 4 }] },
  ],
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      const any = inst.items.some((it) => Number(a[it.id]) >= 1);
      const flags = [];
      inst.items.forEach((it) => { if (Number(a[it.id]) >= 1) flags.push(`${it.text.split(" (")[0]} used in past year — administer NM-ASSIST`); });
      return {
        score: any ? "Positive" : "Negative", max: null,
        band: any ? "Positive" : "Negative",
        color: any ? "#dc2626" : "#16a34a",
        interpretation: any
          ? "At least one substance used in the past year. Administer the full NIDA-Modified ASSIST for endorsed substances and provide brief intervention."
          : "No past-year substance use reported. Provide reinforcement; re-screen per protocol.",
        flags,
      };
    },
  },
};

const CAGE_AID = {
  id: "cage_aid",
  name: "CAGE-AID",
  fullName: "CAGE Adapted to Include Drugs",
  domain: "Substance Use",
  ageRange: "Adult (18+)",
  estMinutes: 1,
  publicDomain: true,
  federalUse: "Brief alcohol + drug screen",
  prompt: "Considering your use of alcohol and drugs in your lifetime:",
  items: [
    { id: "c", text: "Have you ever felt you ought to Cut down on your drinking or drug use?", options: YES_NO },
    { id: "a", text: "Have people Annoyed you by criticizing your drinking or drug use?", options: YES_NO },
    { id: "g", text: "Have you ever felt bad or Guilty about your drinking or drug use?", options: YES_NO },
    { id: "e", text: "Have you ever had a drink or used drugs first thing in the morning (Eye-opener)?", options: YES_NO },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 1, label: "Negative", color: "#16a34a", interpretation: "Below threshold. Monitor / re-screen." },
      { min: 2, max: 4, label: "Positive", color: "#dc2626", interpretation: "≥2 is clinically significant — perform a full substance-use assessment." },
    ],
  },
};

const CIWA_AR = {
  id: "ciwa_ar",
  name: "CIWA-Ar",
  fullName: "Clinical Institute Withdrawal Assessment for Alcohol, Revised",
  domain: "Alcohol Withdrawal",
  ageRange: "Adult (18+)",
  estMinutes: 5,
  publicDomain: true,
  clinicianRated: true,
  federalUse: "Inpatient/withdrawal-management alcohol withdrawal severity monitoring",
  prompt: "Clinician rates each domain based on observation and patient report.",
  items: [
    { id: "nausea", text: "Nausea/vomiting (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "tremor", text: "Tremor (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "sweats", text: "Paroxysmal sweats (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "anxiety", text: "Anxiety (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "agitation", text: "Agitation (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "tactile", text: "Tactile disturbances (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "auditory", text: "Auditory disturbances (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "visual", text: "Visual disturbances (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "headache", text: "Headache/fullness in head (0–7)", options: [0, 1, 2, 3, 4, 5, 6, 7].map((v) => ({ label: String(v), value: v })) },
    { id: "orientation", text: "Orientation/clouding of sensorium (0–4)", options: [0, 1, 2, 3, 4].map((v) => ({ label: String(v), value: v })) },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 8, label: "Minimal/absent", color: "#16a34a", interpretation: "Minimal withdrawal. Generally no medication; continue monitoring per protocol." },
      { min: 9, max: 15, label: "Mild–moderate", color: "#ca8a04", interpretation: "Mild–moderate withdrawal. Symptom-triggered medication per protocol; reassess frequently." },
      { min: 16, max: 67, label: "Severe", color: "#dc2626", interpretation: "Severe withdrawal — high risk of seizures/DTs. Aggressive treatment and close monitoring." },
    ],
  },
};

const COWS = {
  id: "cows",
  name: "COWS",
  fullName: "Clinical Opiate Withdrawal Scale",
  domain: "Opioid Withdrawal",
  ageRange: "Adult (18+)",
  estMinutes: 5,
  publicDomain: true,
  clinicianRated: true,
  federalUse: "MAT/OUD induction — buprenorphine timing; withdrawal severity",
  prompt: "Clinician rates each item for a patient in withdrawal.",
  items: [
    { id: "pulse", text: "Resting pulse rate", options: [{ label: "≤80 (0)", value: 0 }, { label: "81–100 (1)", value: 1 }, { label: "101–120 (2)", value: 2 }, { label: ">120 (4)", value: 4 }] },
    { id: "sweat", text: "Sweating", options: [{ label: "No report/flushing (0)", value: 0 }, { label: "Subjective only (1)", value: 1 }, { label: "Flushed/moist face (2)", value: 2 }, { label: "Beads of sweat (3)", value: 3 }, { label: "Streaming (4)", value: 4 }] },
    { id: "restless", text: "Restlessness", options: [{ label: "Able to sit still (0)", value: 0 }, { label: "Difficulty sitting still (1)", value: 1 }, { label: "Frequent shifting (3)", value: 3 }, { label: "Unable to sit still (5)", value: 5 }] },
    { id: "pupil", text: "Pupil size", options: [{ label: "Normal (0)", value: 0 }, { label: "Possibly larger (1)", value: 1 }, { label: "Moderately dilated (2)", value: 2 }, { label: "Only rim visible (5)", value: 5 }] },
    { id: "aches", text: "Bone/joint aches", options: [{ label: "Not present (0)", value: 0 }, { label: "Mild diffuse (1)", value: 1 }, { label: "Severe diffuse (2)", value: 2 }, { label: "Rubbing joints (4)", value: 4 }] },
    { id: "gi", text: "GI upset", options: [{ label: "None (0)", value: 0 }, { label: "Stomach cramps (1)", value: 1 }, { label: "Nausea/loose stool (2)", value: 2 }, { label: "Vomiting/diarrhea (3)", value: 3 }, { label: "Multiple episodes (5)", value: 5 }] },
    { id: "tremor", text: "Tremor", options: [{ label: "None (0)", value: 0 }, { label: "Felt, not observed (1)", value: 1 }, { label: "Slight tremor (2)", value: 2 }, { label: "Gross tremor (4)", value: 4 }] },
    { id: "yawning", text: "Yawning", options: [{ label: "None (0)", value: 0 }, { label: "1–2 times (1)", value: 1 }, { label: "≥3 times (2)", value: 2 }, { label: "Several times/min (4)", value: 4 }] },
    { id: "anxiety", text: "Anxiety/irritability", options: [{ label: "None (0)", value: 0 }, { label: "Mild (1)", value: 1 }, { label: "Obviously irritable (2)", value: 2 }, { label: "So irritable participation difficult (4)", value: 4 }] },
    { id: "gooseflesh", text: "Gooseflesh skin", options: [{ label: "Smooth (0)", value: 0 }, { label: "Piloerection felt (3)", value: 3 }, { label: "Prominent gooseflesh (5)", value: 5 }] },
    { id: "runny", text: "Runny nose/tearing", options: [{ label: "None (0)", value: 0 }, { label: "Stuffiness/moist eyes (1)", value: 1 }, { label: "Running nose/tearing (2)", value: 2 }, { label: "Constant (4)", value: 4 }] },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 4, label: "None", color: "#16a34a", interpretation: "No active withdrawal." },
      { min: 5, max: 12, label: "Mild", color: "#65a30d", interpretation: "Mild withdrawal." },
      { min: 13, max: 24, label: "Moderate", color: "#ca8a04", interpretation: "Moderate withdrawal — typically sufficient for buprenorphine induction (≥12)." },
      { min: 25, max: 36, label: "Moderately severe", color: "#ea580c", interpretation: "Moderately severe withdrawal." },
      { min: 37, max: 99, label: "Severe", color: "#dc2626", interpretation: "Severe withdrawal." },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// TRAUMA / PTSD
// ─────────────────────────────────────────────────────────────────────────────
const PCL5 = {
  id: "pcl5",
  name: "PCL-5",
  fullName: "PTSD Checklist for DSM-5",
  domain: "Trauma / PTSD",
  ageRange: "Adult (18+)",
  estMinutes: 7,
  publicDomain: true,
  federalUse: "VA/DoD PTSD measurement-based care; trauma-informed care",
  prompt: "In the past month, how much were you bothered by each problem (in response to a stressful experience)?",
  items: [
    "Repeated, disturbing, and unwanted memories of the stressful experience",
    "Repeated, disturbing dreams of the stressful experience",
    "Suddenly feeling or acting as if the stressful experience were actually happening again",
    "Feeling very upset when something reminded you of the stressful experience",
    "Having strong physical reactions when something reminded you of the stressful experience",
    "Avoiding memories, thoughts, or feelings related to the stressful experience",
    "Avoiding external reminders of the stressful experience",
    "Trouble remembering important parts of the stressful experience",
    "Having strong negative beliefs about yourself, other people, or the world",
    "Blaming yourself or someone else for the stressful experience or what happened after it",
    "Having strong negative feelings such as fear, horror, anger, guilt, or shame",
    "Loss of interest in activities that you used to enjoy",
    "Feeling distant or cut off from other people",
    "Trouble experiencing positive feelings",
    "Irritable behavior, angry outbursts, or acting aggressively",
    "Taking too many risks or doing things that could cause you harm",
    "Being 'superalert' or watchful or on guard",
    "Feeling jumpy or easily startled",
    "Having difficulty concentrating",
    "Trouble falling or staying asleep",
  ].map((text, i) => ({ id: `q${i + 1}`, text, options: PCL5_SCALE })),
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 30, label: "Below threshold", color: "#16a34a", interpretation: "Below provisional PTSD cutoff. Continue to monitor." },
      { min: 31, max: 32, label: "At threshold", color: "#ca8a04", interpretation: "At/near provisional cutoff (33). Clinical evaluation indicated." },
      { min: 33, max: 80, label: "Probable PTSD", color: "#dc2626", interpretation: "Meets provisional PTSD cutoff (≥33). Conduct full PTSD diagnostic evaluation (e.g., CAPS-5)." },
    ],
  },
};

const PCPTSD5 = {
  id: "pcptsd5",
  name: "PC-PTSD-5",
  fullName: "Primary Care PTSD Screen for DSM-5",
  domain: "Trauma / PTSD",
  ageRange: "Adult (18+)",
  estMinutes: 2,
  publicDomain: true,
  federalUse: "VA primary-care PTSD pre-screen",
  prompt: "In the past month, have you...",
  items: [
    { id: "q1", text: "Had nightmares about the event(s) or thought about the event(s) when you did not want to?", options: YES_NO },
    { id: "q2", text: "Tried hard not to think about the event(s) or went out of your way to avoid situations that reminded you of the event(s)?", options: YES_NO },
    { id: "q3", text: "Been constantly on guard, watchful, or easily startled?", options: YES_NO },
    { id: "q4", text: "Felt numb or detached from people, activities, or your surroundings?", options: YES_NO },
    { id: "q5", text: "Felt guilty or unable to stop blaming yourself or others for the event(s) or any problems the event(s) may have caused?", options: YES_NO },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 2, label: "Negative", color: "#16a34a", interpretation: "Below cutoff. Monitor." },
      { min: 3, max: 5, label: "Positive", color: "#dc2626", interpretation: "Positive screen (≥3). Administer PCL-5 / full PTSD evaluation." },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// BIPOLAR
// ─────────────────────────────────────────────────────────────────────────────
const MDQ = {
  id: "mdq",
  name: "MDQ",
  fullName: "Mood Disorder Questionnaire (Bipolar screen)",
  domain: "Bipolar Spectrum",
  ageRange: "Adult (18+)",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "Bipolar disorder screening prior to antidepressant initiation",
  prompt: "Has there ever been a period of time when you were not your usual self and...",
  items: [
    ...[
      "you felt so good or hyper that other people thought you were not your normal self, or you were so hyper that you got into trouble?",
      "you were so irritable that you shouted at people or started fights or arguments?",
      "you felt much more self-confident than usual?",
      "you got much less sleep than usual and found you didn't really miss it?",
      "you were much more talkative or spoke faster than usual?",
      "thoughts raced through your head or you couldn't slow your mind down?",
      "you were so easily distracted by things around you that you had trouble concentrating or staying on track?",
      "you had much more energy than usual?",
      "you were much more active or did many more things than usual?",
      "you were much more social or outgoing than usual?",
      "you were much more interested in sex than usual?",
      "you did things that were unusual for you or that other people might have thought were excessive, foolish, or risky?",
      "spending money got you or your family into trouble?",
    ].map((text, i) => ({ id: `q${i + 1}`, text, options: YES_NO })),
    { id: "cooccur", text: "If you checked YES to more than one of the above, have several of these ever happened during the same period of time?", options: YES_NO, excludeFromScore: true },
    { id: "impair", text: "How much of a problem did any of these cause you (e.g., work, family, money, legal trouble)?", excludeFromScore: true, options: [{ label: "No problem", value: 0 }, { label: "Minor problem", value: 1 }, { label: "Moderate problem", value: 2 }, { label: "Serious problem", value: 3 }] },
  ],
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      let symptomCount = 0;
      for (let i = 1; i <= 13; i++) if (Number(a[`q${i}`]) === 1) symptomCount += 1;
      const cooccur = Number(a.cooccur) === 1;
      const impair = Number(a.impair) >= 2;
      const positive = symptomCount >= 7 && cooccur && impair;
      return {
        score: symptomCount, max: 13,
        band: positive ? "Positive" : "Negative",
        color: positive ? "#dc2626" : "#16a34a",
        interpretation: positive
          ? "Positive screen (≥7 symptoms + co-occurrence + moderate/serious impairment). Conduct full diagnostic evaluation for bipolar spectrum disorder."
          : `Negative screen (symptoms: ${symptomCount}/13, co-occurrence: ${cooccur ? "yes" : "no"}, impairment: ${impair ? "yes" : "no"}). All three criteria required.`,
        flags: positive ? ["Positive bipolar screen — evaluate before prescribing antidepressant monotherapy"] : [],
      };
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ADHD (adult + pediatric)
// ─────────────────────────────────────────────────────────────────────────────
const ASRS = {
  id: "asrs",
  name: "ASRS v1.1",
  fullName: "Adult ADHD Self-Report Scale (Part A screener)",
  domain: "ADHD (Adult)",
  ageRange: "Adult (18+)",
  estMinutes: 3,
  publicDomain: true,
  federalUse: "WHO adult ADHD screening (Part A 6-item)",
  prompt: "How often have you been bothered by each of these over the past 6 months?",
  items: [
    { id: "q1", text: "Trouble wrapping up the final details of a project, once the challenging parts are done?", scaleThreshold: 2 },
    { id: "q2", text: "Difficulty getting things in order when you have to do a task requiring organization?", scaleThreshold: 2 },
    { id: "q3", text: "Problems remembering appointments or obligations?", scaleThreshold: 2 },
    { id: "q4", text: "When you have a task requiring a lot of thought, do you avoid or delay getting started?", scaleThreshold: 3 },
    { id: "q5", text: "Fidget or squirm with your hands or feet when you have to sit down for a long time?", scaleThreshold: 3 },
    { id: "q6", text: "Feel overly active and compelled to do things, like you were driven by a motor?", scaleThreshold: 3 },
  ].map((it) => ({ ...it, options: [{ label: "Never", value: 0 }, { label: "Rarely", value: 1 }, { label: "Sometimes", value: 2 }, { label: "Often", value: 3 }, { label: "Very Often", value: 4 }] })),
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      let darkMarks = 0;
      inst.items.forEach((it) => { if (Number(a[it.id]) >= it.scaleThreshold) darkMarks += 1; });
      const positive = darkMarks >= 4;
      return {
        score: darkMarks, max: 6,
        band: positive ? "Positive" : "Negative",
        color: positive ? "#dc2626" : "#16a34a",
        interpretation: positive
          ? "Positive screen (≥4 shaded responses). Symptoms highly consistent with adult ADHD — full clinical evaluation warranted."
          : "Below threshold. Fewer than 4 shaded responses.",
        flags: positive ? ["Positive adult ADHD screen — clinical evaluation"] : [],
      };
    },
  },
};

const VANDERBILT = {
  id: "vanderbilt",
  name: "Vanderbilt (Parent)",
  fullName: "NICHQ Vanderbilt Assessment Scale — Parent Informant",
  domain: "ADHD (Pediatric)",
  ageRange: "Child (6–12)",
  estMinutes: 10,
  publicDomain: true,
  federalUse: "AAP-recommended pediatric ADHD diagnosis & monitoring; EPSDT",
  prompt: "Rate each behavior as observed over the past 6 months (0=Never, 1=Occasionally, 2=Often, 3=Very Often).",
  items: [
    ...[
      "Does not pay attention to details or makes careless mistakes",
      "Has difficulty keeping attention to tasks",
      "Does not seem to listen when spoken to directly",
      "Does not follow through on instructions/fails to finish work",
      "Has difficulty organizing tasks and activities",
      "Avoids or dislikes tasks requiring sustained mental effort",
      "Loses things necessary for tasks/activities",
      "Is easily distracted by extraneous stimuli",
      "Is forgetful in daily activities",
    ].map((text, i) => ({ id: `inatt${i + 1}`, text: `(Inattention) ${text}`, subscale: "inattention" })),
    ...[
      "Fidgets with hands or feet or squirms in seat",
      "Leaves seat when remaining seated is expected",
      "Runs about or climbs excessively in inappropriate situations",
      "Has difficulty playing quietly",
      "Is 'on the go' or acts as if 'driven by a motor'",
      "Talks excessively",
      "Blurts out answers before questions completed",
      "Has difficulty waiting their turn",
      "Interrupts or intrudes on others",
    ].map((text, i) => ({ id: `hyp${i + 1}`, text: `(Hyperactivity/Impulsivity) ${text}`, subscale: "hyperactivity" })),
  ].map((it) => ({ ...it, options: [{ label: "Never (0)", value: 0 }, { label: "Occasionally (1)", value: 1 }, { label: "Often (2)", value: 2 }, { label: "Very Often (3)", value: 3 }] })),
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      const inatt = inst.items.filter((i) => i.subscale === "inattention");
      const hyp = inst.items.filter((i) => i.subscale === "hyperactivity");
      const countSig = (items) => items.filter((it) => Number(a[it.id]) >= 2).length;
      const inattSig = countSig(inatt);
      const hypSig = countSig(hyp);
      const inattPos = inattSig >= 6;
      const hypPos = hypSig >= 6;
      const flags = [];
      if (inattPos) flags.push("Meets symptom count for inattentive presentation (≥6)");
      if (hypPos) flags.push("Meets symptom count for hyperactive/impulsive presentation (≥6)");
      return {
        score: `Inatt ${inattSig}/9 · Hyper ${hypSig}/9`, max: null,
        band: inattPos || hypPos ? "Screen positive" : "Below threshold",
        color: inattPos || hypPos ? "#dc2626" : "#16a34a",
        interpretation: inattPos || hypPos
          ? "Meets symptom-count threshold on ≥1 subscale (≥6 items rated 2 or 3). Confirm performance impairment and pervasiveness across settings (teacher form) for diagnosis."
          : "Does not meet ADHD symptom-count threshold on parent form. Consider teacher form and clinical context.",
        flags,
      };
    },
  },
};

const CRAFFT = {
  id: "crafft",
  name: "CRAFFT 2.1",
  fullName: "CRAFFT Adolescent Substance Use Screen",
  domain: "Substance Use (Adolescent)",
  ageRange: "Adolescent (12–21)",
  estMinutes: 3,
  publicDomain: true,
  federalUse: "Adolescent SBIRT; AAP Bright Futures; EPSDT",
  prompt: "Answer about the past 12 months. (Ask Part A first; if any past-year use, complete the CRAFFT questions.)",
  items: [
    { id: "c", text: "C — Have you ever ridden in a CAR driven by someone (including yourself) who was 'high' or had been using alcohol or drugs?", options: YES_NO },
    { id: "r", text: "R — Do you ever use alcohol or drugs to RELAX, feel better about yourself, or fit in?", options: YES_NO },
    { id: "a", text: "A — Do you ever use alcohol or drugs while you are by yourself, or ALONE?", options: YES_NO },
    { id: "f", text: "F — Do you ever FORGET things you did while using alcohol or drugs?", options: YES_NO },
    { id: "ff", text: "F — Do your FAMILY or FRIENDS ever tell you that you should cut down on your drinking or drug use?", options: YES_NO },
    { id: "t", text: "T — Have you ever gotten into TROUBLE while you were using alcohol or drugs?", options: YES_NO },
  ],
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 1, label: "Lower risk", color: "#16a34a", interpretation: "Lower risk. Provide praise/encouragement and brief advice." },
      { min: 2, max: 6, label: "Higher risk", color: "#dc2626", interpretation: "≥2 indicates higher risk for substance use disorder. Conduct further assessment and brief intervention; consider referral." },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// FUNCTIONING / DISABILITY (DSM-5)
// ─────────────────────────────────────────────────────────────────────────────
const WHODAS = {
  id: "whodas12",
  name: "WHODAS 2.0 (12)",
  fullName: "WHO Disability Assessment Schedule 2.0 (12-item self)",
  domain: "Functioning / Disability",
  ageRange: "Adult (18+)",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "DSM-5 Section III recommended disability measure; functional outcomes",
  prompt: "In the past 30 days, how much difficulty did you have in...",
  items: [
    "Standing for long periods such as 30 minutes?",
    "Taking care of your household responsibilities?",
    "Learning a new task, for example, learning how to get to a new place?",
    "How much of a problem did you have joining in community activities?",
    "How much have you been emotionally affected by your health problems?",
    "Concentrating on doing something for ten minutes?",
    "Walking a long distance such as a kilometre?",
    "Washing your whole body?",
    "Getting dressed?",
    "Dealing with people you do not know?",
    "Maintaining a friendship?",
    "Your day-to-day work/school?",
  ].map((text, i) => ({ id: `q${i + 1}`, text, options: [{ label: "None", value: 0 }, { label: "Mild", value: 1 }, { label: "Moderate", value: 2 }, { label: "Severe", value: 3 }, { label: "Extreme/cannot do", value: 4 }] })),
  scoring: {
    method: "sum",
    bands: [
      { min: 0, max: 12, label: "No–mild disability", color: "#16a34a", interpretation: "Little functional impairment." },
      { min: 13, max: 24, label: "Moderate", color: "#ca8a04", interpretation: "Moderate functional impairment across life domains." },
      { min: 25, max: 36, label: "Severe", color: "#ea580c", interpretation: "Severe functional impairment." },
      { min: 37, max: 48, label: "Extreme", color: "#dc2626", interpretation: "Extreme functional impairment — intensive support indicated." },
    ],
  },
};

const DSM5_L1 = {
  id: "dsm5_l1",
  name: "DSM-5 L1 CC",
  fullName: "DSM-5 Level 1 Cross-Cutting Symptom Measure (Adult)",
  domain: "Cross-Cutting Symptoms",
  ageRange: "Adult (18+)",
  estMinutes: 7,
  publicDomain: true,
  federalUse: "DSM-5 cross-cutting symptom domains; triage to Level 2 measures",
  prompt: "During the past 2 weeks, how much (or how often) have you been bothered by the following?",
  items: [
    ["depression1", "Little interest or pleasure in doing things?", "Depression"],
    ["depression2", "Feeling down, depressed, or hopeless?", "Depression"],
    ["anger", "Feeling more irritated, grouchy, or angry than usual?", "Anger"],
    ["mania1", "Sleeping less than usual but still have a lot of energy?", "Mania"],
    ["mania2", "Starting lots more projects than usual or doing more risky things than usual?", "Mania"],
    ["anxiety1", "Feeling nervous, anxious, frightened, worried, or on edge?", "Anxiety"],
    ["anxiety2", "Feeling panic or being frightened?", "Anxiety"],
    ["anxiety3", "Avoiding situations that make you anxious?", "Anxiety"],
    ["somatic1", "Unexplained aches and pains?", "Somatic"],
    ["somatic2", "Feeling that your illnesses are not being taken seriously enough?", "Somatic"],
    ["si", "Thoughts of actually hurting yourself?", "Suicidal Ideation"],
    ["psychosis1", "Hearing things other people couldn't hear, such as voices?", "Psychosis"],
    ["psychosis2", "Feeling that someone could hear your thoughts, or that you could hear what another person was thinking?", "Psychosis"],
    ["sleep", "Problems with sleep that affected your sleep quality?", "Sleep"],
    ["memory", "Problems with memory or with location/finding your way?", "Memory"],
    ["repetitive", "Unpleasant thoughts/urges/images that repeatedly enter your mind, or feeling driven to perform certain behaviors over and over?", "Repetitive Thoughts/Behaviors"],
    ["dissociation", "Feeling detached or distant from yourself, your body, your surroundings, or your memories?", "Dissociation"],
    ["personality", "Not knowing who you really are or what you want out of life, or not feeling close to other people?", "Personality"],
    ["substance", "Drinking at least 4 drinks in a single day, or using tobacco/medicines beyond prescription/drugs?", "Substance Use"],
  ].map(([id, text, sub]) => ({ id, text, subscale: sub, options: [{ label: "None / Not at all", value: 0 }, { label: "Slight / Rare", value: 1 }, { label: "Mild / Several days", value: 2 }, { label: "Moderate / More than half", value: 3 }, { label: "Severe / Nearly every day", value: 4 }] })),
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      // Any domain rated ≥2 (mild) is a positive that may warrant a Level 2 measure
      // — except Suicidal Ideation, Psychosis, and Substance Use where ≥1 is a flag.
      const positives = [];
      const flags = [];
      const seen = new Set();
      inst.items.forEach((it) => {
        const v = Number(a[it.id]) || 0;
        const lowThreshold = ["Suicidal Ideation", "Psychosis", "Substance Use"].includes(it.subscale);
        const threshold = lowThreshold ? 1 : 2;
        if (v >= threshold && !seen.has(it.subscale)) {
          positives.push(it.subscale);
          seen.add(it.subscale);
          if (lowThreshold) flags.push(`${it.subscale} endorsed — requires immediate clinical attention / Level 2 assessment`);
        }
      });
      return {
        score: `${positives.length} domain${positives.length === 1 ? "" : "s"} positive`, max: null,
        band: positives.length ? positives.join(", ") : "No domains above threshold",
        color: flags.length ? "#dc2626" : positives.length ? "#ca8a04" : "#16a34a",
        interpretation: positives.length
          ? `Domains warranting follow-up (Level 2 measures): ${positives.join(", ")}.`
          : "No domains met the threshold for further inquiry.",
        flags,
      };
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ANTIPSYCHOTIC MONITORING
// ─────────────────────────────────────────────────────────────────────────────
const AIMS = {
  id: "aims",
  name: "AIMS",
  fullName: "Abnormal Involuntary Movement Scale (Tardive Dyskinesia)",
  domain: "Medication Monitoring",
  ageRange: "All ages",
  estMinutes: 10,
  publicDomain: true,
  clinicianRated: true,
  federalUse: "Required tardive-dyskinesia monitoring for patients on antipsychotics (CMS F-tag / quality)",
  prompt: "Clinician rates observed involuntary movements (0=None, 1=Minimal, 2=Mild, 3=Moderate, 4=Severe).",
  items: [
    ["facial", "Muscles of facial expression"],
    ["lips", "Lips and perioral area"],
    ["jaw", "Jaw"],
    ["tongue", "Tongue"],
    ["upper", "Upper extremities (arms, wrists, hands, fingers)"],
    ["lower", "Lower extremities (legs, knees, ankles, toes)"],
    ["trunk", "Neck, shoulders, hips (trunk)"],
    ["severity", "Severity of abnormal movements overall"],
    ["incapacit", "Incapacitation due to abnormal movements"],
    ["awareness", "Patient's awareness of abnormal movements"],
  ].map(([id, text]) => ({ id, text, options: [0, 1, 2, 3, 4].map((v) => ({ label: ["None", "Minimal", "Mild", "Moderate", "Severe"][v], value: v })) })),
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      // Sum items 1–7 (the movement items). Items 8–10 are global/contextual.
      const movementIds = ["facial", "lips", "jaw", "tongue", "upper", "lower", "trunk"];
      const total = movementIds.reduce((s, id) => s + (Number(a[id]) || 0), 0);
      const twoModerate = movementIds.filter((id) => Number(a[id]) >= 2).length >= 2;
      const oneSevere = movementIds.some((id) => Number(a[id]) >= 3);
      const positive = twoModerate || oneSevere;
      return {
        score: total, max: 28,
        band: positive ? "Possible TD" : "No significant dyskinesia",
        color: positive ? "#dc2626" : "#16a34a",
        interpretation: positive
          ? "Schooler-Kane threshold met (≥2 areas rated mild, or ≥1 area rated moderate/severe). Evaluate for tardive dyskinesia; review antipsychotic regimen."
          : "Below clinical threshold for tardive dyskinesia. Continue routine monitoring (e.g., every 6–12 months).",
        flags: positive ? ["Possible tardive dyskinesia — clinical review of antipsychotic"] : [],
      };
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SOCIAL DRIVERS OF HEALTH (HRSN)
// ─────────────────────────────────────────────────────────────────────────────
const SDOH = {
  id: "sdoh",
  name: "SDOH / HRSN",
  fullName: "Health-Related Social Needs Screening (CMS AHC HRSN core domains)",
  domain: "Social Drivers of Health",
  ageRange: "All ages",
  estMinutes: 5,
  publicDomain: true,
  federalUse: "CMS Accountable Health Communities HRSN screening; Joint Commission SDOH; eCQM SDOH-2",
  prompt: "These questions help us understand needs that affect your health. Your answers will not affect your care.",
  items: [
    { id: "housing", text: "What is your living situation today?", options: [{ label: "I have a steady place to live", value: 0 }, { label: "I have a place but am worried about losing it", value: 1 }, { label: "I do not have a steady place to live", value: 1 }] },
    { id: "housing_problems", text: "Do you have problems with your housing (pests, mold, lead, no heat, no utilities)?", options: [{ label: "No", value: 0 }, { label: "Yes", value: 1 }] },
    { id: "food1", text: "Within the past 12 months, you worried that your food would run out before you got money to buy more.", options: [{ label: "Never true", value: 0 }, { label: "Sometimes true", value: 1 }, { label: "Often true", value: 1 }] },
    { id: "food2", text: "Within the past 12 months, the food you bought just didn't last and you didn't have money to get more.", options: [{ label: "Never true", value: 0 }, { label: "Sometimes true", value: 1 }, { label: "Often true", value: 1 }] },
    { id: "transport", text: "In the past 12 months, has lack of reliable transportation kept you from medical appointments, work, or getting things needed for daily living?", options: [{ label: "No", value: 0 }, { label: "Yes", value: 1 }] },
    { id: "utilities", text: "In the past 12 months, has the electric, gas, oil, or water company threatened to shut off services in your home?", options: [{ label: "No", value: 0 }, { label: "Yes", value: 1 }] },
    { id: "safety", text: "Do you feel physically and emotionally safe where you currently live?", options: [{ label: "Yes", value: 0 }, { label: "No", value: 1 }, { label: "Unsure", value: 1 }] },
    { id: "ipv", text: "In the past 12 months, have you been hurt, insulted, threatened, or felt afraid of a partner or person you live with?", options: [{ label: "No", value: 0 }, { label: "Yes", value: 1 }] },
  ],
  scoring: {
    method: "custom",
    fn: function (a, inst) {
      const needs = [];
      const flags = [];
      if (Number(a.housing) >= 1 || Number(a.housing_problems) >= 1) needs.push("Housing instability");
      if (Number(a.food1) >= 1 || Number(a.food2) >= 1) needs.push("Food insecurity");
      if (Number(a.transport) >= 1) needs.push("Transportation");
      if (Number(a.utilities) >= 1) needs.push("Utilities");
      if (Number(a.safety) >= 1) needs.push("Safety");
      if (Number(a.ipv) >= 1) { needs.push("Interpersonal violence"); flags.push("Interpersonal-violence concern — follow safety protocol & resources"); }
      return {
        score: `${needs.length} need${needs.length === 1 ? "" : "s"} identified`, max: null,
        band: needs.length ? needs.join(", ") : "No HRSN identified",
        color: flags.length ? "#dc2626" : needs.length ? "#ca8a04" : "#16a34a",
        interpretation: needs.length
          ? `Identified health-related social needs: ${needs.join(", ")}. Offer navigation/referral to community resources and document in care plan.`
          : "No health-related social needs identified on this screen.",
        flags,
      };
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// LICENSED / SCAFFOLDED — require organizational license to populate item content
// ─────────────────────────────────────────────────────────────────────────────
const LICENSED_STUBS = [
  { id: "cans", name: "CANS", fullName: "Child and Adolescent Needs and Strengths", domain: "Pediatric Comprehensive", licensed: true, federalUse: "State Medicaid (many states) child SED level-of-care & outcomes", note: "Requires TCOM/Praed Foundation certification + license. Attach licensed item set to enable." },
  { id: "ansa", name: "ANSA", fullName: "Adult Needs and Strengths Assessment", domain: "Adult Comprehensive", licensed: true, federalUse: "State Medicaid adult level-of-care & outcomes", note: "Requires TCOM/Praed Foundation certification + license." },
  { id: "asam", name: "ASAM Criteria", fullName: "ASAM Level-of-Care (6 dimensions)", domain: "SUD Level of Care", licensed: true, federalUse: "SUD placement / medical necessity (most state Medicaid SUD benefits)", note: "ASAM Criteria content is copyrighted. Attach licensed dimension content to enable structured intake." },
  { id: "moca", name: "MoCA", fullName: "Montreal Cognitive Assessment", domain: "Cognition", licensed: true, federalUse: "Cognitive screening (training/registration required since 2020)", note: "Requires MoCA certification. Attach licensed form to enable." },
];

// ─────────────────────────────────────────────────────────────────────────────
// REGISTRY
// ─────────────────────────────────────────────────────────────────────────────
export const INSTRUMENTS = [
  PHQ9, PHQ2, GDS15, EPDS,
  GAD7,
  CSSRS,
  AUDITC, AUDIT, DAST10, NIDA_QS, CAGE_AID, CIWA_AR, COWS,
  PCL5, PCPTSD5,
  MDQ,
  ASRS, VANDERBILT, CRAFFT,
  WHODAS, DSM5_L1,
  AIMS,
  SDOH,
];

export const LICENSED_INSTRUMENTS = LICENSED_STUBS;

export const DOMAINS = [...new Set(INSTRUMENTS.map((i) => i.domain))];

export function getInstrument(id) {
  return INSTRUMENTS.find((i) => i.id === id) || LICENSED_STUBS.find((i) => i.id === id) || null;
}

// Score any instrument generically. ctx may carry { sex } for sex-specific tools.
export function scoreInstrument(inst, answers, ctx = {}) {
  if (!inst || !inst.scoring) return null;
  if (inst.scoring.method === "custom") return inst.scoring.fn(answers, inst, ctx);
  if (inst.scoring.method === "sum") return sumScorer(inst.scoring.bands, { flagItem: inst.scoring.flagItem })(answers, inst);
  return null;
}
